import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import { getAccessToken } from 'utils/auth';
import { currencyFormatter } from 'utils/functions';
import { getClaimState, getRedeemState, PROMO_CONNECTION_MESSAGE, reconcileClaims } from './promoVerify';
import RedeemPromoModal from './RedeemPromoModal';

type Transaction = {
  id: number;
  transactionId: string;
  type: string;
  description: string;
  amount: number | string;
  transactionDate: string;
  transactionDateLocal: string;
  transactionTimestamp: string;
  category?: string;
  status?: string;
  isClaimed: number;
  isExpired: number;
  isTicket: number;
};

type HistoryFilter = 'All' | 'Deposit' | 'Withdrawal' | 'Transfer' | 'Winning';

// Promo: promotion payouts such as weekly cash back (EN-11332), credited with the promotion as source.
const CREDIT_TYPES = [
  'Deposit',
  'Game Exit',
  'Rewards',
  'Promo',
  'Refund',
  'Winnings',
  'Unregister',
  // EN-11151: support refunding a freeze purchase (money back to the balance that paid).
  'Streak Freeze Refund'
];
const VALID_TRANSACTION_TYPES = [
  'Transfer',
  'Deposit',
  'Withdraw',
  'Rewards',
  'Promo',
  'Refund',
  'Deduct',
  'Winnings',
  'Payment Card',
  'Tournament Add Chips',
  // EN-11151: freezes bought with Promo or Cash (matches 'Streak Freeze Refund' too).
  'Streak Freeze'
] as const;
const HISTORY_FILTERS: HistoryFilter[] = ['All', 'Deposit', 'Withdrawal', 'Transfer', 'Winning'];
// The backoffice stops accepting a promo claim 24 hours after the deposit.
const CLAIM_WINDOW_MS = 24 * 60 * 60 * 1000;
/** Upper bound for a history read so a stalled connection cannot leave it loading. */
const HISTORY_REQUEST_TIMEOUT_MS = 15000;

const HistoryView = () => {
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<HistoryFilter>('All');
  const [page, setPage] = useState(0);
  const [redeemTarget, setRedeemTarget] = useState<Transaction | null>(null);
  const PAGE_SIZE = 20;
  // Only the newest history request may update state, so a slow pre-claim
  // response cannot overwrite the refreshed (claimed) rows.
  const latestRequestRef = useRef(0);
  const needsFocusRestoreRef = useRef(false);

  const fetchTransactions = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    const isStale = () => requestId !== latestRequestRef.current;
    setLoading(true);
    setError('');

    const base =
      process.env.REACT_APP_BACKOFFICE_URL ||
      `https://backoffice.${process.env.REACT_APP_DOMAIN || 'hijack.poker'}`;

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;

    try {
      // getAccessToken can throw on a missing or malformed session cookie, and the
      // expiry/resume refetch runs unattended, so it must settle loading too.
      const jwt = getAccessToken();
      if (!jwt || !playerGUID) {
        setLoading(false);
        setError('Not authenticated.');
        return;
      }

      // Bound the read (including the body): the expiry/resume refetch is unattended,
      // and a stalled connection must end in the retryable error, not endless loading.
      timer = setTimeout(() => controller.abort(), HISTORY_REQUEST_TIMEOUT_MS);
      const res = await fetch(
        `${base}/api/ledger/get-transactions?playerGUID=${encodeURIComponent(
          playerGUID
        )}&token=${encodeURIComponent(jwt)}`,
        { headers: { Authorization: `Bearer ${jwt}` }, signal: controller.signal }
      );

      // A non-2xx reply (e.g. a backoffice 500) or a malformed payload is a FAILED
      // refresh, not an empty history. Keep the rows already loaded and surface a
      // retryable error; otherwise a failed refresh right after a successful claim
      // would show "No transactions found" and hide the deposit that was just paid.
      if (!res.ok) throw new Error(`History request failed: HTTP ${res.status}`);

      const json = await res.json();
      if (isStale()) return;
      const data = json?.data;
      if (!Array.isArray(data)) throw new Error('History response was not a list');

      reconcileClaims(data);
      setTransactions(data);
    } catch (e) {
      if (isStale()) return;
      console.error('[History] Fetch failed:', e);
      setError('Could not load transaction history.');
    } finally {
      if (timer) clearTimeout(timer);
      if (!isStale()) setLoading(false);
    }
  }, [playerGUID]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Eligibility comes from the backoffice flags at fetch time, so an open view
  // would keep offering Redeem after the 24h window closes. Refetch at the
  // nearest expiry, and on resume (timers can be suspended while hidden).
  useEffect(() => {
    const expiries = transactions
      .filter((tx) => getRedeemState(tx) === 'redeem')
      .map((tx) => Number(tx.transactionTimestamp) * 1000 + CLAIM_WINDOW_MS)
      // Only deadlines still ahead: a row the ledger still calls open after its
      // timestamp-based deadline (the backoffice uses the notification time)
      // must not schedule an endless refetch loop.
      .filter((at) => Number.isFinite(at) && at > Date.now());
    if (expiries.length === 0) return;
    const nextExpiry = Math.min(...expiries);

    const refetchIfExpired = () => {
      if (!document.hidden && Date.now() >= nextExpiry) fetchTransactions();
    };
    const timer = setTimeout(refetchIfExpired, Math.max(0, nextExpiry - Date.now()) + 1000);
    document.addEventListener('visibilitychange', refetchIfExpired);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', refetchIfExpired);
    };
  }, [transactions, fetchTransactions]);

  // After the promo dialog closes, a claim refresh may have replaced the Redeem
  // button that opened it; move focus to a live header control instead of <body>.
  useEffect(() => {
    if (redeemTarget || loading || !needsFocusRestoreRef.current) return;
    needsFocusRestoreRef.current = false;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    containerRef.current
      ?.querySelector<HTMLElement>('[data-cashier-history-header] button:not([disabled])')
      ?.focus();
  }, [redeemTarget, loading]);

  useEffect(() => {
    if (!listRef.current || loading) return;
    const items = listRef.current.querySelectorAll('.history-item');
    gsap.fromTo(
      items,
      { opacity: 0, x: -10 },
      { opacity: 1, x: 0, duration: 0.3, stagger: 0.03, ease: 'power2.out' }
    );
  }, [transactions, activeFilter, page, loading]);

  useEffect(() => {
    setPage(0);
  }, [activeFilter, transactions]);

  const isInboundTransfer = (tx: Transaction) =>
    tx.type?.includes('Transfer') && tx.description?.trim().startsWith('From Player:');

  const isCredit = (tx: Transaction) => {
    const amt = parseFloat(String(tx.amount).replace(/^-+/, '-'));
    if (isInboundTransfer(tx)) return true;
    if (amt < 0) return false;
    return CREDIT_TYPES.includes(tx.type);
  };

  const parseAmount = (tx: Transaction): number => {
    // Handle amounts like "--20.00" (double dash from crypto withdrawals)
    const raw = String(tx.amount).replace(/^-+/, '-');
    return parseFloat(raw) || 0;
  };

  const isValidTransactionType = (tx: Transaction) =>
    VALID_TRANSACTION_TYPES.some((type) => tx.type?.includes(type));

  const isPendingTransaction = (tx: Transaction) => tx.status?.toLowerCase() === 'pending';

  const matchesFilter = (tx: Transaction) => {
    if (!isValidTransactionType(tx)) return false;
    if (activeFilter === 'All') return true;
    if (activeFilter === 'Deposit') {
      return tx.type?.includes('Deposit') || tx.type?.includes('Payment Card');
    }
    if (activeFilter === 'Withdrawal') {
      return tx.type?.includes('Withdraw') || tx.type?.includes('Withdrawal');
    }
    return tx.type?.includes(activeFilter);
  };

  const filteredTransactions = transactions.filter(matchesFilter).sort((a, b) => {
    if (isPendingTransaction(a) === isPendingTransaction(b)) return 0;
    return isPendingTransaction(a) ? -1 : 1;
  });
  const pagedTransactions = filteredTransactions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filteredTransactions.length / PAGE_SIZE);

  return (
    <div ref={containerRef} style={containerStyle}>
      {/* Fixed header — stays put while only the records list below scrolls
          (Unity parity). Kept out of the scroll region entirely (flexShrink:0)
          so there's no sticky/scroll-padding gap above it. */}
      <div style={fixedHeaderStyle} data-cashier-history-header>
        <div style={headerRow}>
          <span style={headerStyle}>Transaction History</span>
          <button onClick={fetchTransactions} style={refreshBtn} disabled={loading}>
            <RefreshIcon
              style={{
                fontSize: '0.9rem',
                ...(loading ? { animation: 'spin 1s linear infinite' } : {})
              }}
            />
          </button>
        </div>

        <div style={filterRowStyle}>
          {HISTORY_FILTERS.map((filter) => {
            const isActive = filter === activeFilter;

            return (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                style={{
                  ...filterBtnStyle,
                  ...(isActive ? activeFilterBtnStyle : null)
                }}>
                {filter === 'All' ? 'All' : filter === 'Winning' ? 'Winnings' : `${filter}s`}
              </button>
            );
          })}
        </div>

        {!loading && filteredTransactions.length > 0 && (
          <div style={countStyle}>{filteredTransactions.length} transactions</div>
        )}
      </div>

      <div style={scrollRegionStyle} data-cashier-history-scroll>
        {loading && (
          <div style={centeredStyle}>
            <div style={spinnerStyle} />
            <span style={loadingText}>Loading transactions...</span>
          </div>
        )}

        {error && !loading && (
          <div style={centeredStyle} role='alert'>
            <span style={{ fontSize: '0.8rem', color: '#c0392b' }}>
              {/* With rows still on screen, promo status is stale until a retry succeeds. */}
              {transactions.length > 0 ? PROMO_CONNECTION_MESSAGE : error}
            </span>
            <button type='button' onClick={fetchTransactions} style={redeemBtnStyle}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && filteredTransactions.length === 0 && (
          <div style={centeredStyle}>
            <span style={emptyText}>No transactions found.</span>
          </div>
        )}

        {!loading && filteredTransactions.length > 0 && (
          <div ref={listRef} style={listStyle}>
          {pagedTransactions.map((tx, i) => {
            const credit = isCredit(tx);
            const pending = isPendingTransaction(tx);
            const amt = parseAmount(tx);
            const absAmt = Math.abs(amt);
            const dateStr = tx.transactionDateLocal || tx.transactionDate || '';
            const claimState = getClaimState(tx.transactionId);
            // A claim the backend confirmed this session reads as Redeemed even if
            // a lagging ledger read still reports the old flags.
            const redeemState = claimState === 'claimed' ? 'redeemed' : getRedeemState(tx);
            const claimPending = claimState === 'pending' || claimState === 'unknown';

            return (
              <div
                key={`${tx.id}-${tx.transactionId}-${i}`}
                className='history-item'
                style={itemStyle}>
                {pending && <div style={pendingSpinnerStyle} />}
                <div
                  style={{
                    ...iconCircle,
                    backgroundColor: credit ? 'rgba(76, 175, 80, 0.15)' : 'rgba(233, 140, 38, 0.15)'
                  }}>
                  {credit ? (
                    <ArrowDownwardIcon style={{ fontSize: '0.9rem', color: '#4CAF50' }} />
                  ) : (
                    <ArrowUpwardIcon style={{ fontSize: '0.9rem', color: '#E98C26' }} />
                  )}
                </div>
                <div style={itemInfoStyle}>
                  <span style={itemTypeStyle}>{tx.type}</span>
                  <span style={itemDescStyle}>{tx.description}</span>
                  <span style={itemDateStyle}>{dateStr}</span>
                </div>
                <div style={itemRightStyle}>
                  <span
                    style={{
                      ...itemAmountStyle,
                      color: credit ? '#4CAF50' : '#E98C26'
                    }}>
                    {credit ? '+' : '-'}
                    {currencyFormatter(absAmt)}
                  </span>
                  {redeemState === 'redeem' && (error || claimPending) && (
                    <button type='button' disabled style={redeemedBtnStyle}>
                      {claimPending ? 'Claim pending' : 'Status unconfirmed'}
                    </button>
                  )}
                  {redeemState === 'redeem' && !error && !claimPending && (
                    <button
                      type='button'
                      onClick={() => setRedeemTarget(tx)}
                      style={redeemBtnStyle}>
                      Redeem
                    </button>
                  )}
                  {redeemState === 'redeemed' && (
                    <button type='button' disabled style={redeemedBtnStyle}>
                      Redeemed
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {filteredTransactions.length > PAGE_SIZE && (
            <div style={paginationRow}>
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                style={{ ...pageBtn, opacity: page === 0 ? 0.3 : 1 }}>
                <ChevronLeftIcon style={{ fontSize: '0.8rem' }} /> Prev
              </button>
              <span style={pageInfo}>
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={(page + 1) * PAGE_SIZE >= filteredTransactions.length}
                style={{
                  ...pageBtn,
                  opacity: (page + 1) * PAGE_SIZE >= filteredTransactions.length ? 0.3 : 1
                }}>
                Next <ChevronRightIcon style={{ fontSize: '0.8rem' }} />
              </button>
            </div>
          )}
          </div>
        )}
      </div>

      {redeemTarget && (
        <RedeemPromoModal
          playerGUID={playerGUID}
          transactionId={redeemTarget.transactionId}
          amount={Math.abs(parseAmount(redeemTarget))}
          onClose={() => {
            needsFocusRestoreRef.current = true;
            setRedeemTarget(null);
          }}
          onClaimed={fetchTransactions}
        />
      )}
    </div>
  );
};

export default HistoryView;

// Fill the Cashier panel's content area so the header can stay fixed while only
// the records list scrolls. minHeight:0 lets the scroll child shrink/scroll.
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  position: 'relative'
};
// Fixed filter header — outside the scroll region (flexShrink:0), so it never
// scrolls and leaves no gap above it (the previous sticky approach left a band
// from the scroll container's top padding).
const fixedHeaderStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  paddingBottom: '12px'
};
// The only scroller — the transaction records.
const scrollRegionStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  scrollbarWidth: 'none'
};
const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};
const headerStyle: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.4)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em'
};
const refreshBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: 'rgba(255,255,255,0.5)',
  padding: '4px 8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};
const filterRowStyle: CSSProperties = {
  display: 'flex',
  gap: '4px',
  flexWrap: 'wrap'
};
const filterBtnStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '999px',
  color: 'rgba(255,255,255,0.6)',
  padding: '6px 10px',
  fontSize: '0.65rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const activeFilterBtnStyle: CSSProperties = {
  background: 'rgba(233, 140, 38, 0.18)',
  border: '1px solid rgba(233, 140, 38, 0.4)',
  color: '#ffffff'
};
const listStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px' };
const centeredStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '30px 0'
};
const spinnerStyle: CSSProperties = {
  width: 30,
  height: 30,
  border: '3px solid rgba(255,255,255,0.1)',
  borderTopColor: '#E98C26',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite'
};
const loadingText: CSSProperties = { fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' };
const emptyText: CSSProperties = { fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)' };

const itemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '10px',
  border: '1px solid rgba(255, 255, 255, 0.04)',
  position: 'relative'
};

const pendingSpinnerStyle: CSSProperties = {
  position: 'absolute',
  right: '8px',
  bottom: '8px',
  width: 12,
  height: 12,
  border: '2px solid rgba(255,255,255,0.12)',
  borderTopColor: '#E98C26',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite'
};

const iconCircle: CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const itemInfoStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  gap: '1px'
};
const itemTypeStyle: CSSProperties = { fontSize: '0.8rem', fontWeight: 600, color: '#ffffff' };
// Wraps rather than truncating: a Rewards row's description carries the promo
// code and its usage terms, which the player needs to read in full.
const itemDescStyle: CSSProperties = {
  fontSize: '0.6rem',
  color: 'rgba(255, 255, 255, 0.45)',
  overflowWrap: 'anywhere'
};
const itemDateStyle: CSSProperties = { fontSize: '0.55rem', color: 'rgba(255, 255, 255, 0.25)' };

const itemRightStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '2px',
  flexShrink: 0
};
const itemAmountStyle: CSSProperties = { fontSize: '0.85rem', fontWeight: 700 };
const redeemBtnStyle: CSSProperties = {
  background: '#E98C26',
  border: 'none',
  borderRadius: '6px',
  color: '#fff',
  fontSize: '0.55rem',
  fontWeight: 700,
  padding: '3px 8px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const redeemedBtnStyle: CSSProperties = {
  ...redeemBtnStyle,
  background: 'rgba(255,255,255,0.15)',
  color: 'rgba(255,255,255,0.6)',
  cursor: 'default'
};
const countStyle: CSSProperties = {
  fontSize: '0.6rem',
  color: 'rgba(255,255,255,0.25)',
  textAlign: 'center',
  marginBottom: '4px'
};
const paginationRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  marginTop: '8px',
  paddingTop: '8px',
  borderTop: '1px solid rgba(255,255,255,0.06)'
};
const pageBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: 'rgba(255,255,255,0.6)',
  fontSize: '0.7rem',
  fontWeight: 600,
  padding: '6px 14px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  display: 'flex',
  alignItems: 'center'
};
const pageInfo: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.4)',
  fontWeight: 600
};
